import React from 'react';
import Icon from '../../../components/AppIcon';
import { useNavigate } from 'react-router-dom';

const HowItWorks = () => {
  const navigate = useNavigate();

  const steps = [
    {
      number: '01',
      icon: 'UserPlus',
      title: 'Create Your Free Account',
      description: 'Register in under 2 minutes. Tell us your interests, preferred courses, and location. No credit card needed.',
      action: 'Register Now',
      actionPath: '/student-registration',
      color: 'cyan',
      gradient: 'from-cyan-500 to-cyan-600',
      bgGradient: 'from-cyan-50 to-cyan-100/50',
      borderColor: 'border-cyan-200',
      textColor: 'text-cyan-700',
      highlight: 'bg-cyan-500',
    },
    {
      number: '02',
      icon: 'Search',
      title: 'Explore & Connect',
      description: 'Browse 500+ institutions, visit virtual booths, watch campus tours, and chat live with admission counselors.',
      action: 'Explore Fair',
      actionPath: '/exhibition-hall',
      color: 'violet',
      gradient: 'from-violet-500 to-violet-600',
      bgGradient: 'from-violet-50 to-violet-100/50',
      borderColor: 'border-violet-200',
      textColor: 'text-violet-700',
      highlight: 'bg-violet-500',
    },
    {
      number: '03',
      icon: 'CalendarCheck',
      title: 'Book & Attend Sessions',
      description: 'Schedule one-on-one video counseling, attend live webinars, and get personalized admission guidance.',
      action: 'View Webinars',
      actionPath: '/auditorium-webinar-hub',
      color: 'amber',
      gradient: 'from-amber-500 to-orange-500',
      bgGradient: 'from-amber-50 to-orange-50/50',
      borderColor: 'border-amber-200',
      textColor: 'text-amber-700',
      highlight: 'bg-amber-500',
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-white relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, #0891B2 1px, transparent 0)',
        backgroundSize: '32px 32px'
      }} />
      <div className="relative max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-violet-500 text-white px-5 py-2 rounded-full mb-5 shadow-md">
            <Icon name="Zap" size={15} color="white" />
            <span className="text-sm font-semibold tracking-wide">How It Works</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-foreground mb-4">
            Your Journey in 3 Simple Steps
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            From registration to finding your dream institution — the entire process takes less than a day.
          </p>
        </div>

        {/* Steps — asymmetric bento layout */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 relative">
          {/* Connector line (desktop only) */}
          <div className="hidden md:block absolute top-16 left-[calc(16.67%+2rem)] right-[calc(16.67%+2rem)] h-0.5 bg-gradient-to-r from-cyan-300 via-violet-300 to-amber-300 z-0" />

          {steps?.map((step, index) => (
            <div
              key={index}
              className={`relative bg-gradient-to-br ${step?.bgGradient} border-2 ${step?.borderColor} rounded-2xl p-7 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group`}
            >
              {/* Step number badge */}
              <div className={`absolute -top-4 left-7 ${step?.highlight} text-white text-xs font-bold px-3 py-1 rounded-full shadow-md`}>
                Step {step?.number}
              </div>

              {/* Icon */}
              <div className={`w-14 h-14 bg-gradient-to-br ${step?.gradient} rounded-2xl flex items-center justify-center mb-5 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                <Icon name={step?.icon} size={26} color="white" />
              </div>

              <h3 className="text-xl font-heading font-bold text-foreground mb-3">
                {step?.title}
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed mb-5">
                {step?.description}
              </p>

              <button
                onClick={() => navigate(step?.actionPath)}
                className={`inline-flex items-center gap-2 ${step?.textColor} font-semibold text-sm hover:gap-3 transition-all duration-200`}
              >
                {step?.action}
                <Icon name="ArrowRight" size={16} color="currentColor" />
              </button>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-14 text-center">
          <button
            onClick={() => navigate('/student-registration')}
            className="inline-flex items-center gap-3 bg-gradient-to-r from-cyan-600 to-violet-600 text-white px-8 py-4 rounded-xl font-semibold text-base shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
          >
            <Icon name="Rocket" size={20} color="white" />
            Start Your Journey Today
          </button>
          <p className="text-sm text-muted-foreground mt-3">Free forever. No credit card required.</p>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
